import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Add, Delete, Edit } from '@mui/icons-material';
import { Allergen, AllergenSource, AllergenStatus, RecipeAllergenSummary, RecipeV2Detail } from '../types';
import AllergenChips from './RecipeV2AllergenChips';
import useRecipeV2Api from '../hooks/useRecipeV2Api';
import { UserContext } from '../../../components/shared/useUser';
import RecipeV2EmptyState from './RecipeV2EmptyState';
import MessagesSnackbar from '../../../components/shared/MessagesSnackbar';
import { normalizeRecipeAllergenStatus } from '../../../app/services/api/recipeV2/allergenStatus';
import RecipeOverrideRemovalDialog from './RecipeOverrideRemovalDialog';

interface AllergensTabProps {
    recipe: RecipeV2Detail;
    onMetadataChange?: (partial: Partial<RecipeV2Detail>) => void;
}

const statusColor: Record<AllergenStatus, 'error' | 'warning' | 'success' | 'default'> = {
  [AllergenStatus.Contains]: 'error',
  [AllergenStatus.MayContain]: 'warning',
  [AllergenStatus.NotPresent]: 'success',
  [AllergenStatus.FreeFrom]: 'success',
  [AllergenStatus.Unknown]: 'default',
};

const statusLabelKey = (status: AllergenStatus) => {
  switch (status) {
    case AllergenStatus.Contains: return 'contains';
    case AllergenStatus.MayContain: return 'mayContain';
    case AllergenStatus.NotPresent: return 'notPresent';
    case AllergenStatus.FreeFrom: return 'freeFrom';
    case AllergenStatus.Unknown:
    default: return 'unknown';
  }
};

// Recipe overrides are user-entered; show "Manual" rather than the raw backend source value.
const sourceLabel = (t: (key: string) => string, source: string) =>
  (source === AllergenSource.RecipeOverride ? t('allergens.sourceManual') : source);

const statusChipColor = (status: AllergenStatus) => statusColor[status] ?? 'default';

const AllergensTab = ({ recipe, onMetadataChange }: AllergensTabProps) => {
  const { t } = useTranslation('recipeV2');
  const api = useRecipeV2Api();
  const { hasPermissionTo } = useContext(UserContext);
  const canWrite = hasPermissionTo(['RecipeV2ComplianceOverride']);
  const [allergens, setAllergens] = useState<RecipeAllergenSummary[]>(recipe.allergenSummary ?? []);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [reference, setReference] = useState<Allergen[]>([]);
  const [selected, setSelected] = useState<Allergen | null>(null);
  const [status, setStatus] = useState<AllergenStatus>(AllergenStatus.Contains);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [deleteCandidate, setDeleteCandidate] = useState<RecipeAllergenSummary | null>(null);
  const [isRemoving, setIsRemoving] = useState(false);
  const [removeReason, setRemoveReason] = useState('');
  const sortedAllergens = [...allergens].sort((left, right) =>
    left.allergenName.localeCompare(right.allergenName) || left.allergenCode.localeCompare(right.allergenCode),
  );

  useEffect(() => setAllergens(recipe.allergenSummary ?? []), [recipe.allergenSummary]);

  const openAdd = async () => {
    setDialogError(null);
    try {
      setReference(await api.getAllergens(recipe.franchiseId));
      setIsEditing(false);
      setSelected(null);
      setStatus(AllergenStatus.Contains);
      setReason('');
      setDialogOpen(true);
    } catch (cause) {
      setReference([]);
      setFeedback({ message: cause instanceof Error ? cause.message : t('feedback.allergenLoadError'), severity: 'error' });
    }
  };

  const openOverride = async (allergen: RecipeAllergenSummary) => {
    setDialogError(null);
    try {
      const list = reference.length ? reference : await api.getAllergens(recipe.franchiseId);
      if (!reference.length) setReference(list);
      setSelected(list.find(item => item.id === allergen.allergenId) ?? null);
      setStatus(normalizeRecipeAllergenStatus(allergen.status));
      setReason('');
      setIsEditing(true);
      setDialogOpen(true);
    } catch (cause) {
      setFeedback({ message: cause instanceof Error ? cause.message : t('feedback.allergenLoadError'), severity: 'error' });
    }
  };

  const saveManual = async () => {
    if (!selected || !reason.trim()) return;
    setSaving(true);
    try {
      await api.overrideAllergen(recipe.id, { allergenId: selected.id, overrideStatus: status, reason: reason.trim() });
      const refreshed = await api.getRecipeDetail(recipe.id);
      setAllergens(refreshed.allergenSummary ?? []);
      onMetadataChange?.({ allergenSummary: refreshed.allergenSummary ?? [] });
      setDialogOpen(false);
      setFeedback({ message: isEditing ? t('feedback.allergenOverrideSaved') : t('feedback.allergenAdded'), severity: 'success' });
      setSelected(null);
      setReason('');
      setDialogError(null);
    } catch (cause) {
      setDialogError(cause instanceof Error ? cause.message : t('feedback.allergenSaveError'));
    } finally { setSaving(false); }
  };

  const requestRemove = (allergen: RecipeAllergenSummary) => {
    setDeleteCandidate(allergen);
    setRemoveReason('');
  };

  const confirmRemove = async () => {
    if (!deleteCandidate || !removeReason.trim()) return;
    setIsRemoving(true);
    try {
      await api.clearAllergenOverride(recipe.id, deleteCandidate.allergenId, removeReason.trim());
      const refreshed = await api.getRecipeDetail(recipe.id);
      setAllergens(refreshed.allergenSummary ?? []);
      onMetadataChange?.({ allergenSummary: refreshed.allergenSummary ?? [] });
      setFeedback({ message: t('feedback.allergenOverrideRemoved'), severity: 'success' });
      setDeleteCandidate(null);
      setRemoveReason('');
    } catch (cause) {
      setFeedback({ message: cause instanceof Error ? cause.message : t('feedback.allergenRemoveError'), severity: 'error' });
    } finally { setIsRemoving(false); }
  };

  const renderActions = (allergen: RecipeAllergenSummary) => {
    if (!canWrite) return '—';
    return (
      <Stack direction="row"
        spacing={0.5}
        alignItems="center">
        <Button size="md"
          variant="secondary"
          startIcon={<Edit fontSize="small" />}
          onClick={() => openOverride(allergen)}>{t('allergens.override')}</Button>
        {allergen.source === AllergenSource.RecipeOverride && (
          <IconButton
            size="small"
            color="error"
            aria-label={`Remove ${allergen.allergenName}`}
            onClick={() => requestRemove(allergen)}><Delete fontSize="small" /></IconButton>
        )}
      </Stack>
    );
  };


  return (
    <Stack spacing={3}>
      {/* Summary chips */}
      <Paper variant="outlined"
        sx={{ p: 2 }}>
        <Stack direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 1 }}>
          <Typography variant="textXL">{t('allergens.title')}</Typography>
          {canWrite && <Button size="md"
            startIcon={<Add />}
            onClick={openAdd}>{t('allergens.add')}</Button>}
        </Stack>
        {sortedAllergens.length > 0 ? (
          <Stack direction="row"
            spacing={1}
            flexWrap="wrap">
            <AllergenChips allergens={sortedAllergens}
              showName
              size="medium" />
          </Stack>
        ) : <Typography color="text.secondary"
          variant="textSM">{t('allergens.empty')}</Typography>}
      </Paper>

      {/* Traceability table */}
      <Paper variant="outlined"
        sx={{ p: 2 }}>
        <Stack direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 1 }}>
          <Typography variant="textXL">{t('allergens.traceability')}</Typography>
        </Stack>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('allergens.allergen')}</TableCell>
                <TableCell>{t('statusLabel')}</TableCell>
                <TableCell>{t('source')}</TableCell>
                <TableCell>{t('allergens.introducedBy')}</TableCell>
                <TableCell>{t('allergens.path')}</TableCell>
                <TableCell>{t('actions.title')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedAllergens.length === 0 ? (
                <RecipeV2EmptyState colSpan={6}>{t('allergens.empty')}</RecipeV2EmptyState>
              ) : sortedAllergens.map((allergen) =>
                allergen.introducedBy.length > 0 ? (
                  allergen.introducedBy.map((trace, idx) => (
                    <TableRow key={`${allergen.allergenCode}-${idx}`}>
                      {idx === 0 ? (
                        <>
                          <TableCell rowSpan={allergen.introducedBy.length}>
                            <Chip
                              label={allergen.allergenName}
                              size="small"
                              color={statusChipColor(normalizeRecipeAllergenStatus(allergen.status))}
                            />
                          </TableCell>
                          <TableCell rowSpan={allergen.introducedBy.length}>
                            {t(`allergens.${statusLabelKey(normalizeRecipeAllergenStatus(allergen.status))}`)}
                          </TableCell>
                          <TableCell rowSpan={allergen.introducedBy.length}>
                            {sourceLabel(t, allergen.source)}
                          </TableCell>
                        </>
                      ) : null}
                      <TableCell>{trace.componentDescription}</TableCell>
                      <TableCell>
                        <Typography variant="textXS"
                          color="text.secondary">
                          {trace.path}
                        </Typography>
                      </TableCell>
                      <TableCell>{renderActions(allergen)}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow key={allergen.allergenCode}>
                    <TableCell>
                      <Chip label={allergen.allergenName}
                        size="small"
                        color={statusChipColor(normalizeRecipeAllergenStatus(allergen.status))} />
                    </TableCell>
                    <TableCell>{t(`allergens.${statusLabelKey(normalizeRecipeAllergenStatus(allergen.status))}`)}</TableCell>
                    <TableCell>{sourceLabel(t, allergen.source)}</TableCell>
                    <TableCell colSpan={2}>
                      <Typography variant="textSM"
                        color="text.secondary">—</Typography>
                    </TableCell>
                    <TableCell>{renderActions(allergen)}</TableCell>
                  </TableRow>
                ),
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth>
        <DialogTitle>{isEditing ? t('allergens.overrideTitle') : t('allergens.add')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2}
            sx={{ mt: 1 }}>
            {dialogError && <Typography color="error">{dialogError}</Typography>}
            <Select value={selected?.id ?? ''}
              displayEmpty
              disabled={isEditing}
              onChange={event => setSelected(reference.find(item => item.id === Number(event.target.value)) ?? null)}>
              <MenuItem value=""
                disabled>Allergen</MenuItem>
              {reference.map(item => (
                <MenuItem key={item.id}
                  value={item.id}>{item.name}</MenuItem>
              ))}
            </Select>
            <Select value={status}
              onChange={event => setStatus(event.target.value as AllergenStatus)}>
              <MenuItem value={AllergenStatus.Contains}>{t('allergens.contains')}</MenuItem>
              <MenuItem value={AllergenStatus.MayContain}>{t('allergens.mayContain')}</MenuItem>
              <MenuItem value={AllergenStatus.NotPresent}>{t('allergens.notPresent')}</MenuItem>
              <MenuItem value={AllergenStatus.FreeFrom}>{t('allergens.freeFrom')}</MenuItem>
              <MenuItem value={AllergenStatus.Unknown}>{t('allergens.unknown')}</MenuItem>
            </Select>
            <TextField label="Reason"
              value={reason}
              onChange={event => setReason(event.target.value)}
              required
              multiline
              rows={3} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="secondary"
            onClick={() => setDialogOpen(false)}>{t('actions.cancel')}</Button>
          <Button variant="primary"
            onClick={saveManual}
            disabled={!selected || !reason.trim() || saving}>{t('actions.save')}</Button>
        </DialogActions>
      </Dialog>

      <RecipeOverrideRemovalDialog
        open={Boolean(deleteCandidate)}
        title={t('feedback.removeAllergenOverrideConfirmTitle')}
        message={t('feedback.removeAllergenOverrideConfirmMessage')}
        reason={removeReason}
        isProcessing={isRemoving}
        onReasonChange={setRemoveReason}
        onConfirm={confirmRemove}
        onCancel={() => {
          setDeleteCandidate(null);
          setRemoveReason('');
        }}
      />
      {feedback && (
        <MessagesSnackbar
          open={Boolean(feedback)}
          onClose={() => setFeedback(null)}
          message={feedback.message}
          severity={feedback.severity}
          duration={4000}
        />
      )}
    </Stack>
  );
};

export default AllergensTab;